import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { SpotLoader } from '@nykaa/ui-components/Loader';

import { SHEET_TRANSITION } from 'shared/components/OverlaySheet';
import { useSetSelectedSKUVendor } from 'shared/store/details/hooks/actions';
import { useSelectedSKU } from 'shared/store/details/hooks/selectors';
import { addToCartPlatform } from 'shared/store/cart/services';
import { setCartToast } from 'shared/store/cart/actions';
import { updateAppCartCount } from 'shared/store/app/actions';
import { getStoreId } from 'shared/store/app/selectors';
import { incrementCartCount } from 'shared/components/CartMerge';
import { useCartContext } from 'shared/components/CartProvider/context';
import { TOASTER_TYPES, useToastContext } from 'shared/components/Toast';
import { useStoreIcon, IconName } from 'shared/icons';
import { inPriceFormat } from 'shared/helpers/utils';
import { getSelectedVendorId, resolveVendorId } from 'shared/helpers/multiSeller';
import { DEVICE_TYPE, getDeviceType } from 'shared/helpers/app';
import NFA from 'shared/helpers/analytics/NFAnalytics';
import {
  MULTI_SELLER_POPUP_SUFFIX,
  MULTI_SELLER_SIZE_SELECTED_SHEET_SUFFIX,
} from 'shared/constants/multiSeller';
import { TEXTS } from 'shared/layouts/DetailsPage/components/ProductInfo/components/AddToBagV2/constants';
import { BAG_UPDATED_SUCCESSFULLY, CONFIRM_BUTTON } from 'shared/layouts/DetailsPage/constants';
import { onAddToCartClick } from 'shared/layouts/DetailsPage/analytics';

import { onMultiSellerPopupOpen, onSellerDetailsPopupOpen } from './analytics';

import {
  AddToBagButton,
  BackButton,
  BottomSheet,
  BottomSheetHeader,
  ButtonLabel,
  CloseButton,
  DesktopModal,
  DesktopSheetContent,
  DeliveryText,
  DetailLabel,
  DetailValue,
  SellerDetails,
  SellerName,
  SellerPrice,
  SellerRow,
  SellerRowContent,
  SellerTitle,
  SheetContent,
  LoaderContainer,
} from './styles';

interface MultiSellerBottomSheetProps {
  vendors: Record<string, ProductDetail.IVendorDetails>;
  selectedSKU?: ProductDetail.ISizeOption;
  defaultVendorId?: string;
  onClose: () => void;
  onAddToBagSuccess?: () => void;
  hideToast?: boolean;
  inline?: boolean;
  onBack?: () => void;
  isOnCart?: boolean;
  cartProductId?: string;
  quantity?: number;
  onConfirmSuccess?: () => void;
  setConfirmedSize?: (sizeName?: string) => void;
  isSizeSelector?: boolean;
  onShowPartialOOS?: (availableQty: number, selectedSize?: ProductDetail.ISizeOption) => void;
}

const MultiSellerBottomSheet = ({
  vendors,
  selectedSKU: selectedSKUProp,
  defaultVendorId,
  onClose,
  onAddToBagSuccess,
  hideToast = false,
  inline = false,
  onBack,
  isOnCart = false,
  cartProductId,
  quantity = 1,
  onConfirmSuccess,
  setConfirmedSize,
  isSizeSelector = false,
  onShowPartialOOS,
}: MultiSellerBottomSheetProps) => {
  const hasTrackedPopupOpen = useRef(false);
  const trackedSellerDetailsVendorId = useRef('');
  const setSelectedSKUVendor = useSetSelectedSKUVendor();
  const reduxSelectedSKU = useSelectedSKU();
  const selectedSKU = selectedSKUProp || reduxSelectedSKU;
  const resolvedSelectedVendorId = getSelectedVendorId(selectedSKU, defaultVendorId);
  const resolvedDefaultVendorId = resolveVendorId(vendors, resolvedSelectedVendorId, defaultVendorId);
  const [detailVendorId, setDetailVendorId] = useState(
    Object.keys(vendors).length === 1 ? resolvedDefaultVendorId : ''
  );
  const [loadingVendorId, setLoadingVendorId] = useState('');
  const storeId = useSelector(getStoreId);
  const dispatch = useDispatch<ThunkDispatch<Record<string, never>, unknown, AnyAction>>();
  const { showToast } = useToastContext();
  const {
    refreshCart,
    setSizeReplaceToastValue,
    setSizeReplaceToastType,
    setCartItemRanks,
    cartItemRanks,
    products,
    replaceCartItemSize,
  } = useCartContext();
  const Close = useStoreIcon(IconName.CIRCLE_X_DUOTONE);
  const Back = useStoreIcon(IconName.CHEVRON_LEFT_SMALL);
  const vendor = vendors[detailVendorId];
  const sellerAttributes = [
    { label: 'Sold By', value: vendor?.sold_by || vendor?.vendor_name },
    { label: 'Country of Origin', value: vendor?.country_of_origin },
    {
      label: 'Name of Manufacturer/ Packer/ Importer',
      value: vendor?.manufacturer,
    },
    {
      label: 'Address of Manufacturer/ Packer/ Importer',
      value: vendor?.manufacturer_address,
    },
  ].filter((attribute) => attribute.value);

  const isSellerDetails = Boolean(detailVendorId);
  const hasMultipleSellers = Object.keys(vendors).length > 1;
  const vendorEntries = Object.entries(vendors);
  const orderedVendors = resolvedDefaultVendorId && vendors[resolvedDefaultVendorId]
    ? [
      [resolvedDefaultVendorId, vendors[resolvedDefaultVendorId]] as [string, ProductDetail.IVendorDetails],
      ...vendorEntries.filter(([vendorId]) => vendorId !== resolvedDefaultVendorId),
    ]
    : vendorEntries;
  const isDesktop = getDeviceType() === DEVICE_TYPE.DESKTOP;
  const popupOpenParams = useMemo(() => {
    const availableVendors = Object.entries(vendors);

    return {
      interactionLocation: `${NFA.pageProperties.pagetype}${isSizeSelector ? MULTI_SELLER_SIZE_SELECTED_SHEET_SUFFIX : ''}${hasMultipleSellers ? MULTI_SELLER_POPUP_SUFFIX : ''}`,
      selectedVendorCode: resolvedDefaultVendorId,
      selectedVendorType: vendors[resolvedDefaultVendorId]?.vendor_type || selectedSKU?.vendor_type,
      availableVendorCode: availableVendors.map(([vendorId]) => vendorId),
      availableVendorType: availableVendors.map(([, vendorDetails]) => vendorDetails.vendor_type),
      availableVendorCount: availableVendors.length,
    };
  }, [isSizeSelector, resolvedDefaultVendorId, selectedSKU, vendors, hasMultipleSellers]);

  useEffect(() => {
    if (hasTrackedPopupOpen.current) return;

    onMultiSellerPopupOpen(popupOpenParams);

    hasTrackedPopupOpen.current = true;
  }, [popupOpenParams]);

  useEffect(() => {
    if (!isSellerDetails) {
      trackedSellerDetailsVendorId.current = '';
      return;
    }
    if (trackedSellerDetailsVendorId.current === detailVendorId) return;

    onSellerDetailsPopupOpen({
      ...popupOpenParams,
      isBuyBoxWinner: detailVendorId === resolvedDefaultVendorId,
      selectedVendorCode: detailVendorId,
      selectedVendorType: vendor?.vendor_type || popupOpenParams.selectedVendorType,
    });
    trackedSellerDetailsVendorId.current = detailVendorId;
  }, [detailVendorId, hasMultipleSellers, isSellerDetails, popupOpenParams, vendor, resolvedDefaultVendorId]);

  const onHeaderBack = () => {
    if (isSellerDetails && hasMultipleSellers) {
      setDetailVendorId('');
      return;
    }
    (onBack || onClose)();
  };

  const onAddToBag = (vendorId: string, vendorDetails: ProductDetail.IVendorDetails) => {
    if (!selectedSKU) return;

    setLoadingVendorId(vendorId);
    dispatch(
      addToCartPlatform({
        productId: selectedSKU.id,
        quantity: 1,
        deviceType: isDesktop ? 'WEBSITE' : 'MSITE',
        storeId,
        vendorId,
        vendorType: vendorDetails.vendor_type,
        isBuyBoxWinner: vendorId === resolvedDefaultVendorId,
      })
    ).then((response) => {
      dispatch(updateAppCartCount(response.cartPartial.quantity));
      incrementCartCount();
      onAddToCartClick({
        addToCartLocation: `${NFA.pageProperties.pagetype}${MULTI_SELLER_POPUP_SUFFIX}`,
        productData: selectedSKU,
        selectedVendorCode: vendorId,
        selectedVendorType: vendorDetails.vendor_type,
        availableVendorCode: Object.keys(vendors),
        availableVendorType: Object.values(vendors).map((vendor) => vendor.vendor_type),
        isBuyBoxWinner: vendorId === resolvedDefaultVendorId,
        selectedVendorId: vendorId,
      });
      if (isDesktop) {
        if (!hideToast) {
          showToast({ type: TOASTER_TYPES.SUCCESS, message: TEXTS.ADDED_TO_CART, hideTime: 4000 });
        }
      } else {
        dispatch(setCartToast(TEXTS.ADDED_TO_CART));
        setTimeout(() => dispatch(setCartToast('')), 4000);
      }
      onClose();
      onAddToBagSuccess?.();
    }).catch((error) => {
      setLoadingVendorId('');
      if (isDesktop) {
        showToast({
          type: TOASTER_TYPES.ERROR,
          message: error?.message || TEXTS.ADD_TO_CART_ERROR,
          hideTime: 4000,
        });
      } else {
        showToast({
          type: TOASTER_TYPES.ERROR,
          message: error?.message || TEXTS.ADD_TO_CART_ERROR,
          hideTime: 4000,
        });
      }
    });
  };

  const onConfirmVendor = (vendorId: string, vendorDetails: ProductDetail.IVendorDetails) => {
    if (!selectedSKU || !cartProductId) return;

    setLoadingVendorId(vendorId);
    const cartItem = products.find((product) => product.productId === cartProductId);
    replaceCartItemSize({
      items: [{
        productId: cartProductId,
        quantity,
        replaceBy: selectedSKU.id,
        vendorId: cartItem?.vendorDTO?.vendorId,
        vendorType: cartItem?.vendorDTO?.vendorType,
        replaceByVendorId: vendorId,
        replaceByVendorType: vendorDetails.vendor_type,
      }],
    })
      .then(({ success, message }) => {
        if (!success) {
          const availableStock = Number(vendorDetails.stock_remaining ?? vendorDetails.qty ?? 0);

          if (onShowPartialOOS && availableStock > 0 && quantity > availableStock) {
            setLoadingVendorId('');
            onConfirmSuccess?.();
            onShowPartialOOS(availableStock, {
              ...selectedSKU,
              vendor_id: vendorId,
              vendor_type: vendorDetails.vendor_type,
            });
            return;
          }

          setSizeReplaceToastValue(message || '');
          setSizeReplaceToastType('negative');
          setLoadingVendorId('');
          setTimeout(() => setSizeReplaceToastValue(''), 2000);
          return;
        }

        setSelectedSKUVendor({ vendorId, vendor: vendorDetails });
        setCartItemRanks(
          cartItemRanks.map((item) =>
            item.productId === cartProductId ? { ...item, productId: selectedSKU.id } : item
          )
        );
        refreshCart().then(() => {
          setConfirmedSize?.(selectedSKU.sizeName);
          setSizeReplaceToastValue(BAG_UPDATED_SUCCESSFULLY);
          setSizeReplaceToastType('positive');
          setTimeout(() => setSizeReplaceToastValue(''), 2000);
        });
        onConfirmSuccess?.();
      })
      .catch((error) => {
        setLoadingVendorId('');
        setSizeReplaceToastValue(error?.message || '');
        setSizeReplaceToastType('negative');
        setTimeout(() => setSizeReplaceToastValue(''), 2000);
      });
  };

  const content = (
    <>
      <BottomSheetHeader data-test="multi-seller-header" showBorder>
        {inline || (isSellerDetails && hasMultipleSellers) ? (
          <BackButton
            type="button"
            data-test="multi-seller-back"
            onClick={onHeaderBack}
            aria-label="Back"
          >
            <Back />
          </BackButton>
        ) : (
          <CloseButton
            type="button"
            data-test="multi-seller-close"
            onClick={onClose}
            aria-label="Close"
          >
            <Close />
          </CloseButton>
        )}
        <SellerTitle data-test="multi-seller-title">
          {isSellerDetails ? 'Seller Details' : 'Available Sellers'}
        </SellerTitle>
      </BottomSheetHeader>

      {isSellerDetails ? (
        <SellerDetails data-test="multi-seller-details">
          {sellerAttributes?.map((attribute, index) => (
            <React.Fragment key={`${attribute.label}-${index}`}>
              <DetailLabel data-test="multi-seller-attribute-label">{attribute.label}</DetailLabel>
              <DetailValue data-test="multi-seller-attribute-value">{attribute.value}</DetailValue>
            </React.Fragment>
          ))}
        </SellerDetails>
      ) : (
        orderedVendors.map(([vendorId, vendorDetails], index) => (
          <SellerRow
            key={vendorId}
            isDefaultVendor={vendorId === resolvedDefaultVendorId}
            data-test="multi-seller-row"
          >
            <SellerRowContent data-test="multi-seller-row-content">
              <SellerName
                type="button"
                data-test="multi-seller-name"
                onClick={() => setDetailVendorId(vendorId)}
              >
                Seller {index + 1} : {vendorDetails.vendor_name}
              </SellerName>
              {vendorDetails.edd_message && (
                <DeliveryText data-test="multi-seller-delivery">
                  {vendorDetails.edd_message}
                </DeliveryText>
              )}
              <SellerPrice data-test="multi-seller-price">
                ₹{inPriceFormat(vendorDetails.discountedPrice)}{' '}
                {Number(vendorDetails.price) > Number(vendorDetails.discountedPrice) && <s>₹{inPriceFormat(vendorDetails.price)}</s>}
                {Number(vendorDetails.discount) > 0 && <span>{vendorDetails.discount}% Off</span>}
              </SellerPrice>
            </SellerRowContent>
            <AddToBagButton
              type="button"
              data-test="multi-seller-add-to-bag"
              disabled={Boolean(loadingVendorId)}
              onClick={() =>
                isOnCart
                  ? onConfirmVendor(vendorId, vendorDetails)
                  : onAddToBag(vendorId, vendorDetails)
              }
            >
              <ButtonLabel isLoading={loadingVendorId === vendorId}>
                {isOnCart ? CONFIRM_BUTTON : 'Add to Bag'}
              </ButtonLabel>
              {loadingVendorId === vendorId && (
                <LoaderContainer>
                  <SpotLoader size="small" />
                </LoaderContainer>
              )}
            </AddToBagButton>
          </SellerRow>
        ))
      )}
    </>
  );

  if (inline) return <div data-test="multi-seller-inline-content">{content}</div>;

  if (isDesktop) {
    return (
      <DesktopModal
        data-test="multi-seller-desktop-modal"
        onHide={onClose}
        showCrossIcon={false}
        onEscapeHide={false}
        allowBackdropClick={false}
      >
        <DesktopSheetContent data-test="multi-seller-content">{content}</DesktopSheetContent>
      </DesktopModal>
    );
  }

  return (
    <BottomSheet
      navKey="multi-seller"
      transitionFrom={SHEET_TRANSITION.BOTTOM}
      onHide={onClose}
      data-test="multi-seller-bottom-sheet"
    >
      <SheetContent data-test="multi-seller-content">{content}</SheetContent>
    </BottomSheet>
  );
};

export default MultiSellerBottomSheet;
